// src/components/AccountWhatsappButton.js
import React, { useState, useEffect } from 'react';
import { User, Calendar, Key, X, Shield, RefreshCw, MessageCircle } from 'lucide-react';

// Componente para el icono de WhatsApp
export const BrandWhatsapp = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={props.size || 24}
      height={props.size || 24}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={props.className}
    >
      <path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21" />
      <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1Z" />
      <path d="M14 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1Z" />
      <path d="M9.5 13.5c.5 1 1.5 2 2.5 2s2-1 2.5-2" />
    </svg>
  );
};

const AccountWhatsappButton = ({ account, services, clients }) => {
  const [showProfilesModal, setShowProfilesModal] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState({});
  const [showNotificationTypeModal, setShowNotificationTypeModal] = useState(false);
  const [selectedNotificationType, setSelectedNotificationType] = useState('renovaciones');
  const [profilesForNotification, setProfilesForNotification] = useState([]);
  const [templates, setTemplates] = useState({});

  // Cargar plantillas al iniciar
  useEffect(() => {
    const savedTemplates = localStorage.getItem('whatsapp-notification-templates');
    if (savedTemplates) {
      setTemplates(JSON.parse(savedTemplates));
    }
  }, []);

  // Plantillas por defecto
  const defaultTemplates = {
    renovaciones: `📅 RECORDATORIO DE RENOVACIÓN 📅
----------------------------
Hola {{nombre_cliente}},

Tu perfil de {{servicio}} "{{perfil}}" vence el {{fecha_expiracion}}.

Detalles de renovación:
💰 Precio: {{precio}}
⏰ Quedan {{dias_restantes}} días
🔄 Renueva ahora para no perder acceso

📝 Notas: {{observaciones}}
📩 thend - Siempre contigo`,

    cambio_contraseña: `🔐 CAMBIO DE CONTRASEÑA 🔐
----------------------------
Hola {{nombre_cliente}},

Se ha actualizado la información de acceso de tu perfil "{{perfil}}" en {{servicio}}.

NUEVA INFORMACIÓN:
📧 Usuario: {{usuario}}
🔑 Nueva Contraseña: {{nueva_contraseña}}
👤 Perfil: {{perfil}}
🔢 PIN: {{pin}}

📝 Información adicional: {{observaciones}}
⚠️ Guarda esta información en un lugar seguro
📩 thend - Soporte 24/7`,

    garantia: `🛡️ SOPORTE Y GARANTÍA 🛡️
----------------------------
Hola {{nombre_cliente}},

Estamos aquí para ayudarte con tu servicio {{servicio}}.

Información de tu cuenta:
👤 Perfil: {{perfil}}
📧 Usuario: {{usuario}}
🔢 PIN: {{pin}}
📅 Vence: {{fecha_expiracion}}

📝 Detalles de cuenta: {{observaciones}}
🔧 Problemas técnicos: Solucionados en 24h
🔄 Reemplazo de cuenta: Disponible
💬 Soporte: Respuesta inmediata

📩 thend - Tu satisfacción es nuestra prioridad`
  };

  // Función para formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Función para formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Obtener información del servicio
  const getServiceInfo = () => {
    const serviceId = account.service_id || account.serviceId;
    return services.find(s => s.id === parseInt(serviceId)) || { name: 'N/A', price: 0 };
  };

  // Obtener información del cliente
  const getClientInfo = (clientId) => {
    return clients.find(c => c.id === parseInt(clientId)) || { name: 'Cliente', phone: '' };
  };

  // Verificar si un perfil está próximo a expirar (menos de 7 días)
  const isProfileNearExpiry = (profile) => {
    if (!profile.expiryDate && !profile.expiry_date) return false;
    
    const expiryDate = new Date(profile.expiryDate || profile.expiry_date);
    const today = new Date();
    
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 0 && diffDays <= 7; // Próximo a expirar en 7 días o menos
  };

  // Verificar si un perfil ha expirado
  const isProfileExpired = (profile) => {
    if (!profile.expiryDate && !profile.expiry_date) return false;
    
    const expiryDate = new Date(profile.expiryDate || profile.expiry_date);
    const today = new Date();
    
    return expiryDate < today;
  };

  // Obtener perfiles ocupados de la cuenta
  const getOccupiedProfiles = () => {
    if (!account.profiles) return [];
    
    return account.profiles.filter(profile => 
      profile.status === 'ocupado' && (profile.client_id || profile.clientId)
    ).map(profile => {
      const clientId = profile.client_id || profile.clientId;
      const client = getClientInfo(clientId);
      const service = getServiceInfo();
      
      return {
        ...profile,
        clientId: clientId,
        clientName: client.name,
        clientPhone: client.phone,
        serviceName: service.name,
        servicePrice: service.price,
        username: account.username,
        password: account.password
      };
    });
  };

  // Función para reemplazar variables en las plantillas - ACTUALIZADA
  const replaceTemplateVariables = (template, profile) => {
    if (!template) return '';

    // Calcular días restantes hasta expiración
    const getDaysUntilExpiry = (profile) => {
      if (!profile.expiryDate && !profile.expiry_date) return 0;
      
      const expiryDate = new Date(profile.expiryDate || profile.expiry_date);
      const today = new Date();
      
      const diffTime = expiryDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return Math.max(0, diffDays); // No devolver números negativos
    };

    const daysRemaining = getDaysUntilExpiry(profile);
    const service = getServiceInfo();

    const replacements = {
      '{{nombre_cliente}}': profile.clientName || 'Cliente',
      '{{servicio}}': profile.serviceName || 'N/A',
      '{{perfil}}': profile.name || 'Perfil',
      '{{usuario}}': profile.username || account.username || 'N/A',
      '{{nueva_contraseña}}': profile.password || account.password || 'N/A',
      '{{precio}}': formatCurrency(service.price || 0),
      '{{pin}}': profile.pin || 'N/A',
      '{{telefono}}': profile.clientPhone || 'N/A',
      '{{fecha_expiracion}}': formatDate(profile.expiryDate || profile.expiry_date),
      '{{dias_restantes}}': daysRemaining.toString(),
      '{{observaciones}}': account.observation || 'Sin observaciones',  // NUEVA VARIABLE AGREGADA
      '{{dias_expirado}}': Math.max(0, -daysRemaining).toString(),
      '{{contraseña_anterior}}': 'Contraseña anterior',
      '{{tipo_problema}}': 'Problema reportado',
      '{{fecha_reporte}}': formatDate(new Date())
    };

    return template.replace(/\{\{([^{}]+)\}\}/g, (match, key) => {
      return replacements[match] || match;
    });
  };

  // Crear enlace de WhatsApp
  const createWhatsAppLink = (profile, notificationType) => {
    if (!profile.clientPhone) {
      alert('El cliente no tiene teléfono registrado');
      return '';
    }

    const phone = profile.clientPhone.replace(/\s+/g, '').replace(/-/g, '');
    const template = templates[notificationType] || defaultTemplates[notificationType] || '';
    const message = replaceTemplateVariables(template, profile);
    
    return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  };

  // Enviar notificación individual
  const sendNotification = (profile, notificationType) => {
    const link = createWhatsAppLink(profile, notificationType);
    if (link) {
      window.open(link, '_blank');
    }
  };

  // Enviar notificaciones múltiples
  const sendMultipleNotifications = () => {
    let profilesToNotify = profilesForNotification.filter(profile => selectedProfiles[profile.id] !== false);
    
    // Si el tipo es renovación, filtrar solo los perfiles que pueden renovarse
    if (selectedNotificationType === 'renovaciones') {
      profilesToNotify = profilesToNotify.filter(profile => isProfileNearExpiry(profile));
      
      if (profilesToNotify.length === 0) {
        alert('No hay perfiles próximos a expirar en la selección actual');
        return;
      }
    }
    
    if (profilesToNotify.length === 0) {
      alert('Por favor selecciona al menos un perfil');
      return;
    }

    // Enviar notificaciones con un pequeño delay entre cada una
    profilesToNotify.forEach((profile, index) => {
      setTimeout(() => {
        sendNotification(profile, selectedNotificationType);
      }, index * 500); // 500ms de delay entre cada notificación
    });
    
    setShowNotificationTypeModal(false);
    setShowProfilesModal(false);
  };

  // Manejar selección de perfil
  const handleToggleProfileSelection = (profileId) => {
    setSelectedProfiles(prev => ({
      ...prev,
      [profileId]: !prev[profileId]
    }));
  };

  // Manejar selección de todos los perfiles
  const handleToggleSelectAll = () => {
    const allSelected = occupiedProfiles.every(profile => selectedProfiles[profile.id]);
    const newSelection = {};
    
    occupiedProfiles.forEach(profile => {
      newSelection[profile.id] = !allSelected;
    });
    
    setSelectedProfiles(newSelection);
  };

  // Obtener perfiles que pueden renovarse (próximos a expirar)
  const getProfilesForRenewal = () => {
    return occupiedProfiles.filter(profile => isProfileNearExpiry(profile));
  };

  // Abrir modal de tipo de notificación
  const openNotificationTypeModal = (profiles = null) => {
    const selectedProfilesList = profiles || occupiedProfiles.filter(profile => selectedProfiles[profile.id]);
    setProfilesForNotification(selectedProfilesList);
    
    // Si solo hay perfiles que no pueden renovarse, cambiar la selección por defecto
    const renewableProfiles = selectedProfilesList.filter(profile => isProfileNearExpiry(profile));
    if (renewableProfiles.length === 0 && selectedProfilesList.length > 0) {
      setSelectedNotificationType('garantia'); // Cambiar a garantía si no hay perfiles renovables
    } else {
      setSelectedNotificationType('renovaciones'); // Mantener renovaciones como default
    }
    
    setShowNotificationTypeModal(true);
  };

  const occupiedProfiles = getOccupiedProfiles();
  const renewableProfiles = getProfilesForRenewal();
  const service = getServiceInfo();

  return (
    <>
      <button 
        className="text-green-400 hover:text-green-500"
        onClick={() => setShowProfilesModal(true)}
        disabled={occupiedProfiles.length === 0}
        title={occupiedProfiles.length === 0 ? "No hay perfiles ocupados en esta cuenta" : "Notificar clientes de esta cuenta"}
      >
        <BrandWhatsapp size={16} className={occupiedProfiles.length === 0 ? "text-gray-500 cursor-not-allowed" : ""} />
      </button>

      {/* Modal principal - Lista de perfiles ocupados */}
      {showProfilesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-3xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center">
                <BrandWhatsapp size={20} className="text-green-400 mr-2" />
                Perfiles Ocupados - {service.name}
              </h2>
              <button 
                className="text-gray-400 hover:text-white"
                onClick={() => setShowProfilesModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="mb-4 bg-gray-700 rounded-lg p-3">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-300">
                    Cuenta: <span className="text-white">{account.username}</span>
                  </p>
                  <p className="text-xs text-gray-400">
                    Perfiles ocupados: {occupiedProfiles.length} de {account.profiles?.length || 0}
                  </p>
                  {renewableProfiles.length > 0 && (
                    <p className="text-xs text-orange-400">
                      📅 Por renovar: {renewableProfiles.length} perfil(es)
                    </p>
                  )}
                </div>
                
                {occupiedProfiles.length > 1 && (
                  <div className="flex gap-2">
                    <button
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md flex items-center"
                      onClick={handleToggleSelectAll}
                    >
                      {occupiedProfiles.every(profile => selectedProfiles[profile.id]) ? 'Deseleccionar todos' : 'Seleccionar todos'}
                    </button>
                    
                    {Object.values(selectedProfiles).some(Boolean) && (
                      <button
                        className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded-md flex items-center"
                        onClick={() => openNotificationTypeModal()}
                      >
                        <MessageCircle size={14} className="mr-1" />
                        Notificar Seleccionados ({Object.values(selectedProfiles).filter(Boolean).length})
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {occupiedProfiles.length === 0 ? (
              <div className="bg-gray-700 p-8 rounded-lg text-center">
                <p className="text-gray-400">Esta cuenta no tiene perfiles ocupados.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {occupiedProfiles.map((profile) => {
                  const nearExpiry = isProfileNearExpiry(profile);
                  const expired = isProfileExpired(profile);
                  
                  // Determinar colores y estilos según estado
                  let borderColor = 'border-gray-600';
                  let bgColor = 'bg-gray-700';
                  let statusBadge = null;
                  
                  if (expired) {
                    borderColor = 'border-red-600';
                    bgColor = 'bg-gray-700';
                    statusBadge = (
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-1 bg-red-600 text-white text-xs rounded-full">
                          ❌ Expirado
                        </span>
                      </div>
                    );
                  } else if (nearExpiry) {
                    borderColor = 'border-orange-600';
                    bgColor = 'bg-gray-700';
                    statusBadge = (
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-1 bg-orange-600 text-white text-xs rounded-full">
                          📅 Por renovar
                        </span>
                      </div>
                    );
                  }
                  
                  return (
                    <div 
                      key={profile.id} 
                      className={`${bgColor} border ${borderColor} rounded-lg p-4 relative`}
                    >
                      {statusBadge}
                      
                      {/* Checkbox para selección múltiple */}
                      {occupiedProfiles.length > 1 && (
                        <div className="absolute top-2 right-2">
                          <input 
                            type="checkbox" 
                            checked={!!selectedProfiles[profile.id]}
                            onChange={() => handleToggleProfileSelection(profile.id)}
                            className="h-4 w-4"
                          />
                        </div>
                      )}
                      
                      <div className={`mb-3 ${statusBadge ? 'mt-8' : ''}`}>
                        <h3 className="font-bold text-white flex items-center">
                          <User size={16} className="mr-2 text-blue-400" />
                          {profile.name}
                        </h3>
                        <p className="text-sm text-gray-300 mt-1">
                          Cliente: <span className="text-white">{profile.clientName}</span>
                        </p>
                        {profile.clientPhone && (
                          <p className="text-xs text-gray-400">
                            📱 {profile.clientPhone}
                          </p>
                        )}
                      </div>
                      
                      <div className="text-sm space-y-1 mb-3">
                        {profile.pin && (
                          <p className="text-gray-300 flex items-center">
                            <Key size={12} className="mr-1" />
                            PIN: <span className="text-white ml-1">{profile.pin}</span>
                          </p>
                        )}
                        
                        {(profile.expiryDate || profile.expiry_date) && (
                          <p className="text-gray-300 flex items-center">
                            <Calendar size={12} className="mr-1" />
                            Vence: <span className="text-white ml-1">
                              {formatDate(profile.expiryDate || profile.expiry_date)}
                            </span>
                          </p>
                        )}
                      </div>
                      
                      {/* Botones de notificación individual */}
                      <div className="flex flex-col gap-2">
                        {/* Primera fila: Renovación (solo si está próximo a expirar) y Garantía */}
                        <div className="flex gap-2">
                          {nearExpiry && (
                            <button
                              className="flex-1 py-2 px-3 bg-orange-600 hover:bg-orange-700 text-white text-xs rounded-md flex items-center justify-center transition-colors"
                              onClick={() => sendNotification(profile, 'renovaciones')}
                              disabled={!profile.clientPhone}
                              title={!profile.clientPhone ? "Cliente sin teléfono" : "Enviar recordatorio de renovación"}
                            >
                              <RefreshCw size={14} className="mr-1" />
                              Renovación
                            </button>
                          )}
                          
                          <button
                            className="flex-1 py-2 px-3 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded-md flex items-center justify-center transition-colors"
                            onClick={() => sendNotification(profile, 'garantia')}
                            disabled={!profile.clientPhone}
                            title={!profile.clientPhone ? "Cliente sin teléfono" : "Enviar notificación de garantía"}
                          >
                            <Shield size={14} className="mr-1" />
                            Garantía
                          </button>
                        </div>
                        
                        {/* Segunda fila: Nueva Contraseña (siempre disponible) */}
                        <button
                          className="w-full py-2 px-3 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md flex items-center justify-center transition-colors"
                          onClick={() => sendNotification(profile, 'cambio_contraseña')}
                          disabled={!profile.clientPhone}
                          title={!profile.clientPhone ? "Cliente sin teléfono" : "Enviar nueva contraseña"}
                        >
                          <Key size={14} className="mr-1" />
                          Nueva Contraseña
                        </button>
                      </div>
                      
                      {!profile.clientPhone && (
                        <p className="text-xs text-red-400 mt-2 text-center">
                          ⚠️ Cliente sin teléfono registrado
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal para seleccionar tipo de notificación múltiple */}
      {showNotificationTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center">
                <MessageCircle size={20} className="text-green-400 mr-2" />
                Tipo de Notificación
              </h2>
              <button 
                className="text-gray-400 hover:text-white"
                onClick={() => setShowNotificationTypeModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="bg-gray-700 p-3 rounded-lg mb-4">
                <p className="text-sm text-gray-300 mb-2">
                  📊 <strong>Perfiles seleccionados:</strong> {profilesForNotification.length}
                </p>
                
                <div className="flex flex-wrap gap-2">
                  {profilesForNotification.map((profile, index) => {
                    const nearExpiry = isProfileNearExpiry(profile);
                    const expired = isProfileExpired(profile);
                    
                    let badgeColor = 'bg-green-900 text-green-300';
                    let status = 'Activo';
                    
                    if (expired) {
                      badgeColor = 'bg-red-900 text-red-300';
                      status = 'Expirado';
                    } else if (nearExpiry) {
                      badgeColor = 'bg-orange-900 text-orange-300';
                      status = 'Por renovar';
                    }
                    
                    return (
                      <span key={index} className={`text-xs px-2 py-1 rounded-full ${badgeColor}`}>
                        {profile.name} - {profile.clientName} ({status})
                      </span>
                    );
                  })}
                </div>
                
                {/* Mostrar información sobre perfiles renovables */}
                {(() => {
                  const selectedRenewable = profilesForNotification.filter(p => isProfileNearExpiry(p));
                  if (selectedRenewable.length > 0) {
                    return (
                      <div className="mt-2 p-2 bg-orange-800 bg-opacity-30 rounded">
                        <p className="text-xs text-orange-300">
                          📅 {selectedRenewable.length} perfil(es) próximo(s) a expirar pueden renovarse
                        </p>
                      </div>
                    );
                  } else if (profilesForNotification.length > 0) {
                    return (
                      <div className="mt-2 p-2 bg-gray-600 bg-opacity-50 rounded">
                        <p className="text-xs text-gray-400">
                          ℹ️ Ningún perfil seleccionado requiere renovación inmediata
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
              
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-200 mb-2">Selecciona el tipo de notificación:</h3>
                
                {/* Solo mostrar renovación si hay perfiles que pueden renovarse */}
                {profilesForNotification.some(p => isProfileNearExpiry(p)) && (
                  <div className="relative">
                    <input
                      type="radio"
                      id="notification-renovaciones"
                      name="notificationType"
                      value="renovaciones"
                      checked={selectedNotificationType === 'renovaciones'}
                      onChange={(e) => setSelectedNotificationType(e.target.value)}
                      className="sr-only"
                    />
                    <label
                      htmlFor="notification-renovaciones"
                      className={`block w-full p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedNotificationType === 'renovaciones'
                          ? 'bg-orange-900 hover:bg-orange-800 border-white text-white font-semibold shadow-lg transform scale-105'
                          : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 hover:border-gray-500'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-base mb-1 flex items-center">
                            <RefreshCw size={18} className="mr-2" />
                            📅 Renovación
                          </div>
                          <div className="text-sm opacity-80">
                            Recordatorio para renovar suscripción 
                            ({profilesForNotification.filter(p => isProfileNearExpiry(p)).length} perfil(es))
                          </div>
                        </div>
                        
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedNotificationType === 'renovaciones'
                            ? 'border-white bg-white'
                            : 'border-gray-400'
                        }`}>
                          {selectedNotificationType === 'renovaciones' && (
                            <div className="w-2 h-2 rounded-full bg-gray-800"></div>
                          )}
                        </div>
                      </div>
                    </label>
                  </div>
                )}

                <div className="relative">
                  <input
                    type="radio"
                    id="notification-garantia"
                    name="notificationType"
                    value="garantia"
                    checked={selectedNotificationType === 'garantia'}
                    onChange={(e) => setSelectedNotificationType(e.target.value)}
                    className="sr-only"
                  />
                  <label
                    htmlFor="notification-garantia"
                    className={`block w-full p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedNotificationType === 'garantia'
                        ? 'bg-purple-900 hover:bg-purple-800 border-white text-white font-semibold shadow-lg transform scale-105'
                        : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 hover:border-gray-500'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-base mb-1 flex items-center">
                          <Shield size={18} className="mr-2" />
                          🛡️ Garantía/Soporte
                        </div>
                        <div className="text-sm opacity-80">Brindar soporte técnico al cliente</div>
                      </div>
                      
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedNotificationType === 'garantia'
                          ? 'border-white bg-white'
                          : 'border-gray-400'
                      }`}>
                        {selectedNotificationType === 'garantia' && (
                          <div className="w-2 h-2 rounded-full bg-gray-800"></div>
                        )}
                      </div>
                    </div>
                  </label>
                </div>

                <div className="relative">
                  <input
                    type="radio"
                    id="notification-cambio-contraseña"
                    name="notificationType"
                    value="cambio_contraseña"
                    checked={selectedNotificationType === 'cambio_contraseña'}
                    onChange={(e) => setSelectedNotificationType(e.target.value)}
                    className="sr-only"
                  />
                  <label
                    htmlFor="notification-cambio-contraseña"
                    className={`block w-full p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedNotificationType === 'cambio_contraseña'
                        ? 'bg-blue-900 hover:bg-blue-800 border-white text-white font-semibold shadow-lg transform scale-105'
                        : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 hover:border-gray-500'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-base mb-1 flex items-center">
                          <Key size={18} className="mr-2" />
                          🔐 Nueva Contraseña
                        </div>
                        <div className="text-sm opacity-80">Notificar nuevas credenciales de acceso</div>
                      </div>
                      
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedNotificationType === 'cambio_contraseña'
                          ? 'border-white bg-white'
                          : 'border-gray-400'
                      }`}>
                        {selectedNotificationType === 'cambio_contraseña' && (
                          <div className="w-2 h-2 rounded-full bg-gray-800"></div>
                        )}
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <button 
                type="button" 
                className="px-4 py-2 border border-gray-600 rounded-md text-gray-300 hover:bg-gray-700 transition-colors"
                onClick={() => setShowNotificationTypeModal(false)}
              >
                Cancelar
              </button>
              <button 
                type="button" 
                className="px-6 py-2 bg-green-600 hover:bg-green-700 rounded-md text-white flex items-center gap-2 transition-colors"
                onClick={sendMultipleNotifications}
              >
                <BrandWhatsapp size={16} />
                Enviar Notificaciones
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccountWhatsappButton;
